import { useState } from 'react';
import { TextField } from '@mui/material';

interface DateInputProps {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

/**
 * DateInput Component
 * 
 * Accepts both formats:
 * - dd/mm/yyyy (e.g., 25/12/1990)
 * - yyyy-mm-dd (e.g., 1990-12-25)
 * 
 * Stores value internally as yyyy-mm-dd (ISO format)
 */
export function DateInput({
  value = '',
  onChange,
  disabled = false,
  error = false,
  helperText,
}: DateInputProps) {
  const [displayValue, setDisplayValue] = useState(value);

  const parseDate = (input: string): string | null => {
    // Remove any non-digit or non-separator characters
    const cleaned = input.replace(/[^\d/-]/g, '');
    
    // Try to parse dd/mm/yyyy format
    const ddmmyyyyMatch = cleaned.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (ddmmyyyyMatch) {
      const [, day, month, year] = ddmmyyyyMatch;
      const d = parseInt(day, 10);
      const m = parseInt(month, 10);
      const y = parseInt(year, 10);
      
      // Validate date
      if (m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 1900 && y <= 2100) {
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
    
    // Try to parse yyyy-mm-dd format (ISO)
    const yyyymmddMatch = cleaned.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
    if (yyyymmddMatch) {
      const [, year, month, day] = yyyymmddMatch;
      const d = parseInt(day, 10);
      const m = parseInt(month, 10);
      const y = parseInt(year, 10);
      
      // Validate date
      if (m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 1900 && y <= 2100) {
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
    
    return null;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setDisplayValue(inputValue);
    
    // Try to parse the input
    const parsedDate = parseDate(inputValue);
    
    if (parsedDate && onChange) {
      onChange(parsedDate);
    } else if (inputValue === '' && onChange) {
      onChange('');
    }
  };

  const handleBlur = () => {
    // On blur, try to format the display value nicely
    const parsedDate = parseDate(displayValue);
    if (parsedDate) {
      // Convert to dd/mm/yyyy for display
      const [year, month, day] = parsedDate.split('-');
      setDisplayValue(`${day}/${month}/${year}`);
    }
  };

  return (
    <TextField
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      error={error}
      helperText={helperText || 'يوم/شهر/سنة (مثال: 25/12/1990)'}
      placeholder="dd/mm/yyyy"
      fullWidth
      inputProps={{
        dir: 'ltr',
        style: {
          direction: 'ltr',
        },
      }}
      InputProps={{
        sx: {
          direction: 'ltr !important',
          '& input': {
            direction: 'ltr !important',
          },
        },
      }}
      sx={{
        direction: 'ltr !important',
        '& .MuiOutlinedInput-root': {
          direction: 'ltr !important',
        },
      }}
    />
  );
}
